import type { PortalState } from '@/api/types';

/** Ordered customer-facing billing lifecycle. The practitioner drives an
 *  engagement through these; the customer portal reads the same field to
 *  decide whether exports are unlocked (`paid_complete`). */
export const PORTAL_STATES: readonly PortalState[] = [
  'onboarding',
  'processing',
  'awaiting_payment',
  'paid_complete',
];

export const PORTAL_STATE_LABEL: Record<PortalState, string> = {
  onboarding: 'Onboarding',
  processing: 'Processing',
  awaiting_payment: 'Awaiting payment',
  paid_complete: 'Paid / complete',
};

/** One-line description of what each state means to the customer. */
export const PORTAL_STATE_HINT: Record<PortalState, string> = {
  onboarding: 'Customer is uploading data and completing intake.',
  processing: 'Our team is reviewing the data and preparing the findings.',
  awaiting_payment: 'Findings are published; the customer needs to pay to unlock exports.',
  paid_complete: 'Invoice paid. The customer can download the report and CSV.',
};

export interface BillingView {
  state: PortalState;
  stateLabel: string;
  isPaid: boolean;
  hasPaymentLink: boolean;
  /** Exports unlock only once the engagement is both published and paid. */
  exportsUnlocked: boolean;
}

/** Pure derivation of the billing display state from the engagement fields.
 *  Kept out of the component so it's unit-testable. */
export function deriveBillingView(engagement: {
  portal_state: PortalState;
  invoice_paid_at?: string | null;
  agree_payment_url?: string | null;
  published_audit_run_id?: string | null;
}): BillingView {
  const isPaid = engagement.invoice_paid_at != null;
  return {
    state: engagement.portal_state,
    stateLabel: PORTAL_STATE_LABEL[engagement.portal_state],
    isPaid,
    hasPaymentLink: engagement.agree_payment_url != null,
    exportsUnlocked:
      engagement.portal_state === 'paid_complete' &&
      engagement.published_audit_run_id != null,
  };
}

/** Validate a payment link the same way the API does, so the UI can flag a
 *  bad link before the round-trip. Any https host is fine (the provider isn't
 *  constrained); empty is allowed (clears). */
export function isValidPaymentUrl(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed === '') return true;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return false;
  }
  return parsed.protocol === 'https:' && parsed.hostname !== '';
}
